import { Icon } from "@mui/material";
import { Children, FunctionComponent, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import "../css/horizontalList.css";

interface HorizontalListProps {
  title: string;
  subtitle?: string | null;
  action?: ReactNode;
  children: ReactNode;
}

const HorizontalList: FunctionComponent<HorizontalListProps> = (props: HorizontalListProps) => {
  const { title, subtitle, action, children } = props;
  const itemCount = Children.count(children);

  const ref = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, itemCount]);

  const scroll = (direction: "left" | "right") => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: el.clientWidth * 0.8 * (direction === "right" ? 1 : -1),
      behavior: "smooth",
    });
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <section className="nbk-row" aria-label={title}>
      <div className="nbk-row__header">
        <div>
          <h2 className="nbk-row__title serie-title">{title}</h2>
          {subtitle ? <p className="nbk-row__subtitle">{subtitle}</p> : null}
        </div>
        <div className="nbk-row__controls">
          {action}
          {hasOverflow ? (
            <>
              <button
                type="button"
                className="nbk-row__arrow"
                onClick={() => scroll("left")}
                disabled={!canScrollLeft}
                aria-label={`Défiler « ${title} » vers la gauche`}
              >
                <Icon>chevron_left</Icon>
              </button>
              <button
                type="button"
                className="nbk-row__arrow"
                onClick={() => scroll("right")}
                disabled={!canScrollRight}
                aria-label={`Défiler « ${title} » vers la droite`}
              >
                <Icon>chevron_right</Icon>
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div
        className={`nbk-row__viewport${canScrollLeft ? " nbk-row__viewport--fade-left" : ""}${
          canScrollRight ? " nbk-row__viewport--fade-right" : ""
        }`}
      >
        <div ref={ref} className="nbk-row__track" onScroll={updateScrollState}>
          {children}
        </div>
      </div>
    </section>
  );
};

export default HorizontalList;
